import { View, Text, ScrollView, Image, TouchableOpacity, ActivityIndicator, Platform, Keyboard } from 'react-native'
import React, { useEffect, useRef, useState } from 'react'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import { PrivateStackParamList } from '@app/navigationApp/types'
import { ThemedView, ThemedText, Theme } from '@app/theme'
import CommonHeader from '@app/sharedComponents/Header/Header'
import { useTheme } from '@shopify/restyle'
import Button from '@app/sharedComponents/Button/Button'
import InputText from '@app/sharedComponents/TextInput/TextInput'
import EditIcon from '@app/assets/svg/EditIcon'
import { verifyBankAccount } from '../VerifyBankAccount/services/verifyBankService'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import { trackEvent } from '@app/utils'
import { showErrorSheet } from '@app/store/slices/errorSlice'
import { get as getLodash } from 'lodash';


// modal imports
import BankErrorModal from './components/BankErrorModal'
import BankPennyDropModal from './components/BankPennyDropModal'
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet'
import BankSuccessModal from './components/BankSuccessModal'
import BankIfscUnsupportedModal from './components/BankIfscUnsupportedModal'
import { updateCurrentStepId, updateLinkedApplication } from '@app/store/slices/authSlice'
import { getNextRoute } from '@app/navigationApp/navigationUtil'
import Stepper from '@app/sharedComponents/Stepper/Stepper'
import { useStepperData } from '@app/hooks/useStepperData'
import handleApiErr from '@app/helper/handleApiErr'
import { TextInput } from 'react-native-gesture-handler'
import { ThemedTouchableOpacity } from '@app/theme/Components'
import rupeeLottie from '../../assets/lotties/Penny_drop.json'

type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'BANK_ACCOUNT_ADD'>;

type ModalState = 'error' | 'success' | 'penny' | 'idle' | 'ifsc_unsupported'

const BankAccountAdd = ({ route, navigation }: ScreenProps) => {

    const dispatch = useAppDispatch()

    const user = useAppSelector(state => state.auth?.userDetails);

    const { stepperData, isLoading: stepperLoading } = useStepperData()

    const applicationId = user?.linkedApplications[0]?.applicationId

    const showHome = useAppSelector(state => state.platformParams.showHome);

    const [accountNumber, setAccountNumber] = useState('')
    const [ifsc, setIfsc] = useState('')
    const [modalState, setModalState] = useState<ModalState>('idle')
    // const [rupeeLottie, setRupeeLottie] = useState<any>(null)
    const [successLottie, setSuccessLottie] = useState<any>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const { bankCode, bankName, ifsc: receivedIfsc } = route.params

     const ref = useRef<TextInput>(null)
    
        useEffect(() => {
            if(Platform.OS !== 'web' && !stepperLoading && !receivedIfsc) {
                const timeout = setTimeout(() => {
                    if(ref?.current) {
                Keyboard.dismiss()

                        ref?.current?.focus()
                    }
                },500)
    
                return () => {
                    clearTimeout(timeout)
                }
            }
        } ,[stepperLoading])

    useEffect(() => {
        if (receivedIfsc) {
            setIfsc(receivedIfsc)
        }
    }, [receivedIfsc])

    const [logo, setLogo] = useState<string>(`https://volt-images.s3.ap-south-1.amazonaws.com/bank-logos/pngs/${bankCode.substring(0, 4)}.png`)


    const isDisabled = accountNumber.length < 1 || ifsc.length < 1

    useEffect(() => {
        // fetch("https://lottie.host/78420adf-df0a-4fa2-a500-36bbf2ac0762/YkUlAx1j0n.json", {
        //     method: "GET",
        // })
        //     .then((response) => response.json())
        //     .then((responseData) => {
        //         setRupeeLottie(responseData)
        //     })
        //     .catch((error) => {
        //         console.error(error);
        //     })

        fetch("https://lottie.host/34e6bfb8-a91f-4e4f-8856-7571d41cee8e/6sPdPdx4Yf.json", {
            method: "GET",
        })
            .then((response) => response.json())
            .then((responseData) => {
                setSuccessLottie(responseData)
            })
            .catch((error) => {
                console.error(error);
            })
    }, [])

    const handleVerifyBankAccount = async () => {
        trackEvent('VERIFY_BANK_BUTTON_CLICKED')
        setModalState('penny')
        try {
            const response = await verifyBankAccount({
                applicationId: applicationId,
                bankAccountNumber: accountNumber,
                bankIfscCode: ifsc
            })
            const stepResponseObject = response?.data?.stepResponseObject;
            const updatedApplication = response?.data?.updatedApplicationObj;

            // update the linked application in redux
            dispatch(updateLinkedApplication(updatedApplication))

            if (stepResponseObject?.failureDetails?.failureType === 'NAME_MISMATCH') {
                // close modal
                setModalState('idle')
                setTimeout(() => {
                    if(showHome){
                    navigation.reset({
                        index: 0,
                        routes: [{ name: 'HOME' }, { name: 'NAME_MISMATCH', params: { bankCode: bankCode , mismatchData: stepResponseObject , accountNumber: accountNumber } }],
                    })
                    } else {
                        navigation.reset({
                            index: 0,
                            routes: [{ name: 'KYC_STEPPER' }, { name: 'NAME_MISMATCH', params: { bankCode: bankCode , mismatchData: stepResponseObject , accountNumber: accountNumber } }],
                        })
                    }
                }, 500)
            } else if (stepResponseObject?.failureDetails?.failureType === 'IFSC_INELIGIBLE_FOR_AUTOPAY') {
                // show error modal
                setModalState('ifsc_unsupported')
            }
            else {
                setModalState('success')
                const currentStepId = getLodash(response, 'data.updatedApplicationObj.currentStepId', null)
                dispatch(updateCurrentStepId(currentStepId))
            }
        } catch (error: any) {
            setModalState('idle')
            if (handleApiErr(error)) {
                dispatch(showErrorSheet({
                    isErrorSheetVisible: true,
                    closeOnBackdropPress: false,
                    errorType: 'ERROR',
                    errorMessage: error?.data?.message || 'Something went wrong',
                    ctaLabel: 'Try again',
                }))
            } 
        }
    }


    const navigateToNextScreen = () => {
        if (showHome) {
            navigation.reset({
                index: 0,
                routes: [{ name: 'HOME' }, { name: getNextRoute(user?.linkedApplications[0].currentStepId, user?.linkedApplications[0].applicationState, user?.linkedApplications[0].stepStatusMap) }]
            })
        } else {
            navigation.reset({
                index: 0,
                routes: [{ name: 'KYC_STEPPER' }, { name: getNextRoute(user?.linkedApplications[0].currentStepId, user?.linkedApplications[0].applicationState, user?.linkedApplications[0].stepStatusMap) }]
            })
        }
    }

    const handleAddAnotherBankAccount = () => {
        setModalState('idle')
        navigation.navigate('BANK_SELECT')
    }




    const { colors } = useTheme<Theme>()

    if (stepperLoading) {
        return <ThemedView justifyContent='center' alignItems='center' flex={1} bg='bg-surface-primary'>
            <ActivityIndicator size={12} color={colors['text-action-primary']} />
        </ThemedView>
    }
    return (
        <ThemedView flex={1} bg='bg-surface-primary'>
            <CommonHeader hideBottomBorder label='Enter account details' navigation={navigation} pageName={route.name} />
            <Stepper stepperData={stepperData} />
            <ThemedView flex={1} bg='bg-surface-primary'>
                <ScrollView keyboardShouldPersistTaps="handled">
                    <ThemedView padding='lg' paddingTop='2xl'>
                        <ThemedView pb='md' flexDirection='row' alignItems='center' justifyContent='space-between'>
                            <ThemedView mr='lg' flexDirection='row' alignItems='center' flex={1}>
                                <Image onError={() => {
                                    setLogo(`https://volt-images.s3.ap-south-1.amazonaws.com/bank-logos/pngs/default.png`)
                                }} source={{ uri: logo }} style={{ width: 40, height: 40 }} />
                                <ThemedText ml="md" variant="b2s" numberOfLines={1} ellipsizeMode="tail" style={{ flexShrink: 1 }}>
      {bankName}
    </ThemedText>
                            </ThemedView>
                            <TouchableOpacity onPress={() => navigation.goBack()}>
                                <EditIcon size={20} color={colors['text-action-primary']} />
                            </TouchableOpacity>
                        </ThemedView>
                        <ThemedView mt='xs' mb='md'>
                            <InputText
                                ref={ref}
                                placeHolder={'Account Number'}
                                keyboardType="default"
                                label="Enter account number"
                                showLabel={true}
                                autoFocus={Platform.OS === 'web'}
                                value={accountNumber}
                                showAnimation
                                isDarkMode={false}
                                onChangeText={(e) => {
                                    if (e !== " ") {
                                        setAccountNumber(e.replace(/[^0-9]/g, ''))
                                    }
                                }}
                                onFocus={() => {
                                    trackEvent('ADDBANK_ACCOUNTNUMBER_FOCUSED')
                                }}
                                showErrorMessage={false}
                                showErrorMessageInFocus={true}
                            />
                        </ThemedView>
                        <ThemedTouchableOpacity onPress={() =>{
                            navigation.navigate('BANK_BRANCH_SEARCH', { bankCode })
                        } }>
                            <ThemedView pointerEvents='none'>
                                <InputText
                                    placeHolder={'Search by branch or IFSC'}
                                    keyboardType="default"
                                    showLabel={true}
                                    label='IFSC'
                                    value={ifsc}
                                    isDarkMode={false}
                                    onFocus={() => {
                                        trackEvent('ADDBANK_IFSC_FOCUSED')
                                        navigation.navigate('BANK_BRANCH_SEARCH', { bankCode })
                                    }}
                                    showErrorMessage={true}
                                    showErrorMessageInFocus={true}
                                    autoFocus={false}
                                />
                            </ThemedView>
                        </ThemedTouchableOpacity>
                    </ThemedView>
                </ScrollView>
                <ThemedView
                    style={{ width: '100%' }}
                    bg='bg-fill-primary'
                    padding='lg'
                    borderTopWidth={1}
                    borderTopColor='border-primary'
                >
                    <ThemedText textAlign='center' variant='b4r' color='text-secondary' mb="lg">
                        We'll match account holder name with PAN records
                    </ThemedText>
                    <Button
                        eventName='BANK_ACCOUNT_ADD_VERIFY_BANK_ACCOUNT'
                        label="Verify bank account"
                        type="SOLID"
                        size='MD'
                        disabled={isDisabled}
                        handlePress={handleVerifyBankAccount}
                        bg={colors['bg-action-primary']}
                        textColor={colors['text-primary']}
                    />

                </ThemedView>
                <BottomSheetContainer
                    isVisible={modalState !== 'idle'}
                    close={() => setModalState('idle')}
                    onClose={() => setModalState('idle')}
                    dragComponent={<View style={{ width: 40, height: 4, backgroundColor: colors['border-primary'], borderRadius: 10 }} />}
                    containerStyle={{ borderTopRightRadius: 16, borderTopLeftRadius: 16 }}
                    closeOnBackdropPress={true}
                    borderTopRightRadius={10}
                    borderTopLeftRadius={10}
                >
                    {modalState === 'error' && <BankErrorModal errorMessage={errorMessage ?? ''} onPress={handleAddAnotherBankAccount} />}
                    {modalState === 'success' && <BankSuccessModal lottie={successLottie} onPress={navigateToNextScreen} />}
                    {modalState === 'penny' && <BankPennyDropModal lottie={rupeeLottie} />}
                    {modalState === 'ifsc_unsupported' && <BankIfscUnsupportedModal />}
                </BottomSheetContainer>
            </ThemedView>
        </ThemedView>
    )
}

export default BankAccountAdd